import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Loader2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import {
  GET_STUDENT_AVATAR_CREDITS,
  GET_STUDENT_AVATAR_GALLERY,
} from "@/graphql/queries/students";
import { ACTIVATE_AVATAR, GENERATE_AVATAR } from "@/graphql/mutations/students";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Everything the avatar generator can be asked for. A style is always chosen by
 * whoever is spending the credits — the app never picks one for them, wherever
 * the offer appears.
 */
export const AVATAR_STYLES = [
  { key: "default", label: "Numatytasis", emoji: "🎨", description: "Mielas kawaii personažas" },
  { key: "realistic", label: "Realistinis", emoji: "🦊", description: "Tikroviška gyvūno nuotrauka" },
  { key: "digital_art", label: "Skaitmeninis", emoji: "💻", description: "Moderni skaitmeninė iliustracija" },
  { key: "cartoon", label: "Animacinis", emoji: "📺", description: "Klasikinis animacinis stilius" },
  { key: "watercolor", label: "Akvarelė", emoji: "🎨", description: "Švelni akvarelės tapyba" },
  { key: "pixel_art", label: "Pikseliai", emoji: "👾", description: "Retro žaidimų stilius" },
  { key: "anime", label: "Anime", emoji: "✨", description: "Anime/manga stilius" },
  { key: "sticker", label: "Lipdukas", emoji: "🏷️", description: "Ryškus lipduko dizainas" },
  { key: "fantasy", label: "Fantazija", emoji: "🔮", description: "Stebuklinga fantazijos iliustracija" },
] as const;

const AVATARS_PER_PAGE = 12;

export type StudioAvatar = {
  id: string;
  url: string;
  isActive: boolean;
  createdAt?: string;
};

type StudioOptions = {
  /** Refetch whatever else on the page shows the avatar. */
  onChanged?: () => void;
  onGenerated?: (avatar: StudioAvatar) => void;
  /** The gallery is a second round trip; a page that only generates can skip it. */
  withGallery?: boolean;
  /** Off where the new avatar is shown on the page itself and a toast would be noise. */
  successToast?: boolean;
};

/**
 * Credits, gallery and the two mutations behind them, so every place that offers
 * an avatar offers exactly the same thing.
 */
export function useAvatarStudio(studentId: string | undefined, options: StudioOptions = {}) {
  const { onChanged, onGenerated, withGallery = true, successToast = true } = options;

  const { data: creditsData, refetch: refetchCredits } = useQuery(GET_STUDENT_AVATAR_CREDITS, {
    variables: { id: studentId },
    skip: !studentId,
    fetchPolicy: "cache-and-network",
  });

  const { data: galleryData, refetch: refetchGallery } = useQuery(GET_STUDENT_AVATAR_GALLERY, {
    variables: { id: studentId },
    skip: !studentId || !withGallery,
  });

  const refresh = () => {
    refetchCredits();
    if (withGallery) refetchGallery();
    onChanged?.();
  };

  const [generateAvatar, { loading: isGenerating }] = useMutation(GENERATE_AVATAR, {
    onCompleted: (data: any) => {
      const avatar = data?.generateAvatar?.avatar;
      if (avatar) {
        if (successToast) toast.success("Avataras sėkmingai sugeneruotas!");
        refresh();
        onGenerated?.(avatar);
      } else {
        toast.error(data?.generateAvatar?.errors?.join(", ") || "Nepavyko sukurti avataro");
      }
    },
    onError: (error) => toast.error(`Klaida: ${error.message}`),
  });

  const [activateAvatar, { loading: isActivating }] = useMutation(ACTIVATE_AVATAR, {
    onCompleted: (data: any) => {
      if (data?.activateAvatar?.avatar) {
        toast.success("Avataras aktyvuotas");
        refresh();
      } else {
        toast.error(data?.activateAvatar?.errors?.join(", ") || "Nepavyko aktyvuoti");
      }
    },
    onError: (error) => toast.error(`Klaida: ${error.message}`),
  });

  return {
    credits: (creditsData as any)?.studentAvatarCredits?.imagesAvailable ?? 0,
    gallery: ((galleryData as any)?.studentAvatarGallery ?? []) as StudioAvatar[],
    isGenerating,
    isActivating,
    generate: (style: string) => {
      if (!studentId) return;
      generateAvatar({ variables: { studentId, style } });
    },
    activate: (avatarId: string) => activateAvatar({ variables: { id: avatarId } }),
  };
}

/** "avatarą / avatarus / avatarų" — Lithuanian counts the noun, not the digit. */
export function avatarsLabel(count: number): string {
  const lastTwo = count % 100;
  const last = count % 10;
  if (lastTwo >= 11 && lastTwo <= 19) return `${count} avatarų`;
  if (last === 1) return `${count} avatarą`;
  if (last === 0) return `${count} avatarų`;
  return `${count} avatarus`;
}

/** The style menu. Nothing is generated until one of these is tapped. */
export function AvatarStyleDialog({
  open,
  onOpenChange,
  credits,
  isGenerating,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  credits: number;
  isGenerating: boolean;
  onPick: (style: string) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Pasirinkite avataro stilių</DialogTitle>
          <DialogDescription>
            Kiekvienas stilius sukuria unikalų avatarą. Kaina: 10 užduočių.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2">
          {AVATAR_STYLES.map((style) => (
            <button
              key={style.key}
              className="flex flex-col items-center gap-1 rounded-lg border-2 border-border p-3 transition-all hover:border-primary hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
              disabled={isGenerating || credits <= 0}
              onClick={() => {
                onPick(style.key);
                onOpenChange(false);
              }}
            >
              <span className="text-2xl">{style.emoji}</span>
              <span className="text-xs font-medium">{style.label}</span>
              <span className="text-[10px] text-muted-foreground text-center leading-tight">
                {style.description}
              </span>
            </button>
          ))}
        </div>
        {isGenerating && (
          <div className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Generuojama...
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Every avatar ever generated, with the one in use marked. */
export function AvatarGalleryDialog({
  open,
  onOpenChange,
  gallery,
  credits,
  isGenerating,
  isActivating,
  onGenerateClick,
  onActivate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gallery: StudioAvatar[];
  credits: number;
  isGenerating: boolean;
  isActivating: boolean;
  onGenerateClick: () => void;
  onActivate: (avatarId: string) => void;
}) {
  const [page, setPage] = useState(0);

  const totalPages = Math.max(1, Math.ceil(gallery.length / AVATARS_PER_PAGE));
  const currentPage = Math.min(page, totalPages - 1);
  const paginated = gallery.slice(
    currentPage * AVATARS_PER_PAGE,
    (currentPage + 1) * AVATARS_PER_PAGE
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (next) setPage(0);
      }}
    >
      <DialogContent className="sm:max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Avatarų galerija</DialogTitle>
          <DialogDescription>
            Galima sugeneruoti dar {avatarsLabel(credits)}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 mb-2 shrink-0">
          <Button size="sm" onClick={onGenerateClick} disabled={isGenerating || credits <= 0}>
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                Generuojama...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-3.5 w-3.5" />
                Generuoti naują
              </>
            )}
          </Button>
          <span className="text-sm text-muted-foreground">{credits} liko</span>
        </div>
        {gallery.length > 0 ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 overflow-y-auto min-h-0">
            {paginated.map((avatar) => (
              <div
                key={avatar.id}
                className={`relative group rounded-lg overflow-hidden border-2 transition-all ${
                  avatar.isActive
                    ? "border-primary ring-2 ring-primary/20"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <img src={avatar.url} alt="Avatar" className="w-full aspect-square object-cover" />
                {avatar.isActive && (
                  <div className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-0.5">
                    <Check className="h-3 w-3" />
                  </div>
                )}
                {!avatar.isActive && (
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-7 text-xs px-2"
                      onClick={() => onActivate(avatar.id)}
                      disabled={isActivating}
                    >
                      Aktyvuoti
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">
            Dar nėra sugeneruotų avatarų
          </p>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-between shrink-0 pt-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={currentPage === 0}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Ankstesnis
            </Button>
            <span className="text-xs text-muted-foreground">
              {currentPage + 1} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage >= totalPages - 1}
            >
              Kitas
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * The pill under a profile avatar: generate (via the style menu) and browse.
 * Both dialogs are wired here so a page only decides where the pill sits.
 *
 * The style menu can optionally be driven from outside, which is how a
 * notification about an earned avatar lands on the thing itself rather than on a
 * page with the button somewhere on it. Left alone it stays self-contained, as
 * the other two call sites want it.
 */
export function AvatarActions({
  studio,
  className,
  stylesOpen,
  onStylesOpenChange,
}: {
  studio: ReturnType<typeof useAvatarStudio>;
  className?: string;
  stylesOpen?: boolean;
  onStylesOpenChange?: (open: boolean) => void;
}) {
  const [ownStyles, setOwnStyles] = useState(false);
  const [showGallery, setShowGallery] = useState(false);

  const showStyles = stylesOpen ?? ownStyles;
  const setShowStyles = (open: boolean) => {
    setOwnStyles(open);
    onStylesOpenChange?.(open);
  };

  return (
    <>
      <div
        className={
          className ??
          "flex items-center gap-2 rounded-full border bg-background/80 px-2 py-1 shadow-sm backdrop-blur-sm"
        }
      >
        <Button
          variant="ghost"
          size="sm"
          className="h-8 rounded-full px-3"
          onClick={() => setShowStyles(true)}
          disabled={studio.isGenerating || studio.credits <= 0}
          title="Sukurti naują AI avatarą"
        >
          {studio.isGenerating ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="mr-1.5 h-3.5 w-3.5" />
          )}
          <span className="text-xs font-medium">Generuoti</span>
        </Button>
        <span className="text-[10px] font-medium text-muted-foreground">
          {studio.credits} liko
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 rounded-full px-3"
          onClick={() => setShowGallery(true)}
          title="Peržiūrėti visus avatarus"
        >
          <ImageIcon className="mr-1.5 h-3.5 w-3.5" />
          <span className="text-xs font-medium">Galerija</span>
        </Button>
      </div>

      <AvatarStyleDialog
        open={showStyles}
        onOpenChange={setShowStyles}
        credits={studio.credits}
        isGenerating={studio.isGenerating}
        onPick={studio.generate}
      />

      <AvatarGalleryDialog
        open={showGallery}
        onOpenChange={setShowGallery}
        gallery={studio.gallery}
        credits={studio.credits}
        isGenerating={studio.isGenerating}
        isActivating={studio.isActivating}
        onGenerateClick={() => {
          setShowGallery(false);
          setShowStyles(true);
        }}
        onActivate={(avatarId) => {
          studio.activate(avatarId);
          setShowGallery(false);
        }}
      />
    </>
  );
}
